import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Mic, MicOff, Video, VideoOff, PhoneOff, ChevronUp,
  UserPlus, Minimize2, Maximize2, Search, X, Check, Volume2, VolumeX,
} from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import { useDeviceEnumeration, useDevicesStore } from "@/features/devices"
import { playTestTone } from "@/clientVc/views/controlBar/components/hooks/utils"
import { AvatarPhoto } from "./avatar-photo"

interface CallScreenProps {
  name: string
  imageUrl?: string
  /** The other party's contactPresenceId, when the caller has it (the real
   *  `GlobalCallLayer` surface does; the legacy demo path on PresencePage
   *  does not). Looked up against the live directory so a changed picture
   *  shows instead of the copy the ring carried. */
  contactId?: string
  mode: "audio" | "video"
  contacts?: { id: string; name: string; imageUrl?: string }[]
  /**
   * Is this a group call?
   *
   * Gates "Add to call". On a one-to-one call the control offers to manage
   * participants of a conversation that has exactly two and cannot have more,
   * so it is not applicable — which is the bug this closes.
   *
   * Defaults false, and NOTHING sets it true today: `startCall` invites a
   * single `peer.id` and `ActiveCall extends CallPeer`, so every call this app
   * can currently place is one-to-one. The prop exists so the control returns
   * on its own when a multi-party call model does, rather than being rebuilt
   * from memory.
   *
   * Whoever turns it on should know the panel below is not finished: selecting
   * a contact adds their id to a local Set and marks them "In call" without
   * inviting anyone.
   */
  isGroup?: boolean
  onClose: () => void
  /** Controlled connect state (real ring). When provided, the screen shows
   *  "Calling…" until it flips true — instead of the standalone 1.2s demo
   *  connect. Left undefined by legacy/mock callers to keep old behavior. */
  connected?: boolean
}

function Avatar({ name, imageUrl, contactId, size = 96 }: { name: string; imageUrl?: string; contactId?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const colorIdx = name.charCodeAt(0) % colors.length
  return (
    <div
      className={`rounded-full flex items-center justify-center font-semibold shrink-0 overflow-hidden ${colors[colorIdx]}`}
      style={{ width: size, height: size }}
    >
      <AvatarPhoto
        src={imageUrl}
        alt={name}
        className="w-full h-full object-cover"
        fallback={<span style={{ fontSize: size * 0.28 }}>{initials}</span>}
        contactId={contactId}
      />
    </div>
  )
}

function formatDuration(totalSeconds: number) {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

function ControlButton({ active, danger, icon: Icon, label, onClick }: {
  active?: boolean; danger?: boolean; icon: React.ElementType; label: string; onClick?: () => void
}) {
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-2 w-[84px] select-none">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors border
        ${danger
          ? "bg-destructive text-destructive-foreground border-destructive"
          : active
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-white/10 text-white border-white/15 hover:bg-white/20"
        }`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[11px] text-white/80">{label}</span>
    </button>
  )
}

/**
 * Mute toggle plus a microphone picker. Speaker output used to live in a
 * second tab here, but a call screen output control needs to be visible on
 * its own, where people look for it, so it moved out into SpeakerControl below and this
 * one goes back to a single device list, same as before speakers were added.
 */
function AudioControl({
  t, muted, onToggleMute, mics, selectedMicId, setSelectedMicId, canPickMic, onPickerOpen,
}: {
  t: (key: string) => string
  muted: boolean
  onToggleMute: () => void
  mics: MediaDeviceInfo[]
  selectedMicId: string
  setSelectedMicId: (id: string) => void
  canPickMic: boolean
  onPickerOpen: () => void
}) {
  const [audioPickerOpen, setAudioPickerOpen] = useState(false)

  return (
    <div className="flex flex-col items-center gap-2 w-[84px] select-none">
      <div className="relative">
        <button
          type="button"
          onClick={onToggleMute}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors border
            ${muted
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-white/10 text-white border-white/15 hover:bg-white/20"
            }`}
          aria-label={muted ? t("controlBar.turnOnMicrophone") : t("controlBar.turnOffMicrophone")}
        >
          {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>

        {canPickMic && (
          <DropdownMenu
            open={audioPickerOpen}
            onOpenChange={(open) => {
              setAudioPickerOpen(open)
              if (open) onPickerOpen()
            }}
          >
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="absolute -right-1 -bottom-1 w-5 h-5 rounded-full bg-zinc-800 border border-white/20 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors"
                aria-label={t("controlBar.audioDeviceSettings")}
              >
                <ChevronUp className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top" className="z-[300] min-w-[280px] p-2 shadow-xl bg-card text-foreground">
              <div className="px-3 pb-1 pt-2 text-xs text-muted-foreground">{t("controlBar.systemDefault")}</div>
              <DropdownMenuRadioGroup value={selectedMicId} onValueChange={(v) => setSelectedMicId(v)}>
                {mics.map((d) => (
                  <DropdownMenuRadioItem key={d.deviceId} value={d.deviceId} className="cursor-pointer rounded-md px-3 py-2">
                    <span className="truncate">{d.label || t("controlBar.microphone")}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <span className="text-[11px] text-white/80">{muted ? "Unmute" : "Mute"}</span>
    </div>
  )
}

/**
 * Speaker toggle plus an output device picker. The button always shows and
 * always toggles `speakerOn`, since there is no browser support test for
 * "can this call make sound" the way there is for switching outputs; only
 * the chevron/device list is gated behind `canSwitchSpeaker`
 * (`setSinkId` support plus at least one enumerated output).
 *
 * There is no call audio pipeline wired up yet, so this only flips local
 * state; nothing downstream reads `speakerOn` today.
 */
function SpeakerControl({
  t, speakerOn, onToggleSpeaker, speakers, selectedSpeakerId, setSelectedSpeakerId, canSwitchSpeaker, onPickerOpen,
}: {
  t: (key: string) => string
  speakerOn: boolean
  onToggleSpeaker: () => void
  speakers: MediaDeviceInfo[]
  selectedSpeakerId: string
  setSelectedSpeakerId: (id: string) => void
  canSwitchSpeaker: boolean
  onPickerOpen: () => void
}) {
  const [speakerPickerOpen, setSpeakerPickerOpen] = useState(false)

  return (
    <div className="flex flex-col items-center gap-2 w-[84px] select-none">
      <div className="relative">
        <button
          type="button"
          onClick={onToggleSpeaker}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors border
            ${!speakerOn
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-white/10 text-white border-white/15 hover:bg-white/20"
            }`}
          aria-label={speakerOn ? t("controlBar.turnOffSpeaker") : t("controlBar.turnOnSpeaker")}
        >
          {speakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>

        {canSwitchSpeaker && (
          <DropdownMenu
            open={speakerPickerOpen}
            onOpenChange={(open) => {
              setSpeakerPickerOpen(open)
              if (open) onPickerOpen()
            }}
          >
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="absolute -right-1 -bottom-1 w-5 h-5 rounded-full bg-zinc-800 border border-white/20 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors"
                aria-label={t("controlBar.audioDeviceSettings")}
              >
                <ChevronUp className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top" className="z-[300] min-w-[280px] p-2 shadow-xl bg-card text-foreground">
              <div className="px-3 pb-1 pt-2 text-xs text-muted-foreground">{t("controlBar.systemDefault")}</div>
              <DropdownMenuRadioGroup value={selectedSpeakerId} onValueChange={(v) => setSelectedSpeakerId(v)}>
                {speakers.map((d) => (
                  <DropdownMenuRadioItem key={d.deviceId} value={d.deviceId} className="cursor-pointer rounded-md px-3 py-2">
                    <span className="truncate">{d.label || t("controlBar.speaker")}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator className="bg-slate-200" />
              <DropdownMenuItem
                className="cursor-pointer rounded-md px-3 py-2"
                onSelect={() => playTestTone()}
              >
                <Volume2 className="mr-2 h-4 w-4" />
                {t("controlBar.testSpeakers")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <span className="text-[11px] text-white/80">{t("controlBar.speaker")}</span>
    </div>
  )
}

export function CallScreen({ name, imageUrl, contactId, mode, contacts = [], isGroup = false, onClose, connected: connectedProp }: CallScreenProps) {
  const t = useTranslation()
  const { mics, speakers, refresh: refreshDevices } = useDeviceEnumeration()
  const selectedMicId = useDevicesStore((s) => s.selectedMicId)
  const selectedSpeakerId = useDevicesStore((s) => s.selectedSpeakerId)
  const selectMic = useDevicesStore((s) => s.selectMic)
  const selectSpeaker = useDevicesStore((s) => s.selectSpeaker)
  // Nothing to pick from, or (for speakers) the browser can't route audio
  // output at all (Firefox/Safari have no setSinkId): showing the tab then
  // would offer a choice that does nothing.
  const canPickMic = mics.length > 0
  // Until the page has mic permission the browser lists devices with blank
  // names (and often blank ids), and a P2P call never opens the mic itself,
  // so the pickers would show a row of unnamed entries. Ask once when a
  // picker opens, release the mic straight away, and list again.
  const askedForLabelsRef = useRef(false)
  const ensureDeviceLabels = useCallback(() => {
    if (askedForLabelsRef.current) return
    if ([...mics, ...speakers].every((d) => d.label)) return
    if (!navigator.mediaDevices?.getUserMedia) return
    askedForLabelsRef.current = true
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop())
        return refreshDevices()
      })
      .catch(() => {})
  }, [mics, speakers, refreshDevices])
  const canSwitchSpeaker =
    speakers.length > 0 &&
    typeof HTMLMediaElement !== "undefined" &&
    "setSinkId" in HTMLMediaElement.prototype
  const [connectedLocal, setConnectedLocal] = useState(false)
  // Controlled by the real ring when `connectedProp` is passed; otherwise the
  // legacy standalone demo-connect drives it.
  const connected = connectedProp ?? connectedLocal
  const [seconds, setSeconds] = useState(0)
  const [muted, setMuted] = useState(false)
  const [speakerOn, setSpeakerOn] = useState(true)
  const [cameraOn, setCameraOn] = useState(mode === "video")
  const [isMinimized, setIsMinimized] = useState(false)
  const [showAddPanel, setShowAddPanel] = useState(false)
  const [addSearch, setAddSearch] = useState("")
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set())

  // Draggable mini-banner
  const [pos, setPos] = useState({ x: 24, y: 24 })
  const dragging = useRef(false)
  const dragOffset = useRef({ x: 0, y: 0 })
  const bannerRef = useRef<HTMLDivElement>(null)

  const subtitle = useMemo(() => {
    if (!connected) return mode === "video" ? "Video calling…" : "Calling…"
    return formatDuration(seconds)
  }, [connected, mode, seconds])

  const filteredContacts = useMemo(() =>
    contacts.filter(c => c.name !== name && c.name.toLowerCase().includes(addSearch.toLowerCase())),
    [contacts, name, addSearch]
  )

  useEffect(() => {
    // Only the legacy/mock path auto-connects; a controlled call waits for the
    // real `accepted` signal via `connectedProp`.
    if (connectedProp !== undefined) return
    const t = window.setTimeout(() => setConnectedLocal(true), 1200)
    return () => window.clearTimeout(t)
  }, [connectedProp])

  useEffect(() => {
    if (!connected) return
    const i = window.setInterval(() => setSeconds(s => s + 1), 1000)
    return () => window.clearInterval(i)
  }, [connected])

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return
    const bw = bannerRef.current?.offsetWidth ?? 220
    const bh = bannerRef.current?.offsetHeight ?? 72
    setPos({
      x: Math.min(Math.max(0, e.clientX - dragOffset.current.x), window.innerWidth - bw),
      y: Math.min(Math.max(0, e.clientY - dragOffset.current.y), window.innerHeight - bh),
    })
  }
  const onPointerUp = () => { dragging.current = false }

  // Minimized floating banner
  if (isMinimized) {
    return (
      <div
        ref={bannerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="fixed z-[200] select-none touch-none"
        style={{ left: pos.x, top: pos.y }}
      >
        <div className="flex items-center gap-3 bg-zinc-900/95 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl px-3 py-2.5 cursor-grab active:cursor-grabbing min-w-[200px]">
          <div className="relative shrink-0">
            <Avatar name={name} imageUrl={imageUrl} contactId={contactId} size={40} />
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-zinc-900" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">{name}</p>
            <p className="text-[10px] text-white/60 font-mono">{subtitle}</p>
          </div>
          <button onClick={() => setIsMinimized(false)} onPointerDown={e => e.stopPropagation()}
            className="p-1.5 rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors shrink-0" title="Restore">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onClose} onPointerDown={e => e.stopPropagation()}
            className="p-1.5 rounded-full bg-destructive/80 hover:bg-destructive text-white transition-colors shrink-0" title="End call">
            <PhoneOff className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    )
  }

  // Full screen
  return (
    <div className="fixed inset-0 z-[100] flex">
      {/* Main call area */}
      <div className="relative flex-1 flex flex-col">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-800" />

        <div className="relative h-full w-full flex flex-col">
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              <span className="text-sm text-white/80 font-medium">Inmeet Call</span>
              {addedIds.size > 0 && (
                <span className="text-xs text-white/50 ml-1">· {addedIds.size + 1} participants</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setIsMinimized(true)}
                className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors" title="Minimize">
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 flex items-center justify-center px-6 pb-10">
            {mode === "video" ? (
              <div className="w-full max-w-4xl">
                <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-950 aspect-video shadow-2xl">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      {!connected && (
                        <div className="relative inline-flex mb-4">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-primary/30 animate-ping" style={{ transform: "scale(1.6)" }} />
                          <span className="absolute inline-flex h-full w-full rounded-full bg-primary/20 animate-ping" style={{ transform: "scale(2.0)", animationDelay: "300ms" }} />
                          <Avatar name={name} imageUrl={imageUrl} contactId={contactId} size={96} />
                        </div>
                      )}
                      {connected && <Avatar name={name} imageUrl={imageUrl} contactId={contactId} size={96} />}
                      {/* A name with no break point (a 100-char group name,
                          the field's own limit) had nothing to wrap at and
                          nothing here clipped it either, so truncate it like
                          every other single-line name in this screen. */}
                      <p className="mt-5 text-lg font-semibold text-white truncate max-w-xs mx-auto px-2" title={name}>{name}</p>
                      <p className="text-sm text-white/70 mt-1">{subtitle}</p>
                    </div>
                  </div>
                  {/* Added participants row */}
                  {addedIds.size > 0 && (
                    <div className="absolute top-3 left-3 flex gap-2">
                      {[...addedIds].map(id => {
                        const c = contacts.find(x => x.id === id)
                        if (!c) return null
                        return (
                          <div key={id} className="flex flex-col items-center gap-1">
                            <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/20 bg-zinc-900">
                              <AvatarPhoto
                                src={c.imageUrl}
                                alt={c.name}
                                className="w-full h-full object-cover"
                                fallback={<div className="w-full h-full flex items-center justify-center text-white text-xs font-bold">{c.name[0]}</div>}
                                contactId={c.id}
                              />
                            </div>
                            <span className="text-[9px] text-white/70 truncate max-w-[48px]">{c.name.split(" ")[0]}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {/* Self preview */}
                  <div className="absolute bottom-4 right-4 w-[148px] h-[90px] rounded-xl overflow-hidden border border-white/15 bg-zinc-900 shadow-lg">
                    <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-950" />
                    <div className="absolute inset-0 flex items-center justify-center text-white/50 text-xs">You</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="relative inline-flex">
                  {!connected && (
                    <>
                      <span className="absolute inline-flex h-full w-full rounded-full bg-primary/25 animate-ping" style={{ transform: "scale(1.5)", animationDuration: "1.2s" }} />
                      <span className="absolute inline-flex h-full w-full rounded-full bg-primary/15 animate-ping" style={{ transform: "scale(2.0)", animationDuration: "1.6s", animationDelay: "400ms" }} />
                      <span className="absolute inline-flex h-full w-full rounded-full bg-primary/10 animate-ping" style={{ transform: "scale(2.5)", animationDuration: "2s", animationDelay: "800ms" }} />
                    </>
                  )}
                  <Avatar name={name} imageUrl={imageUrl} contactId={contactId} size={120} />
                </div>
                {/* Added participants */}
                {addedIds.size > 0 && (
                  <div className="flex justify-center gap-3 mt-6">
                    {[...addedIds].map(id => {
                      const c = contacts.find(x => x.id === id)
                      if (!c) return null
                      return (
                        <div key={id} className="flex flex-col items-center gap-1">
                          <Avatar name={c.name} imageUrl={c.imageUrl} contactId={c.id} size={48} />
                          <span className="text-[10px] text-white/70">{c.name.split(" ")[0]}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
                {/* Unlike the video branch above, this one isn't boxed in an
                    `overflow-hidden` frame: a name with no break point would
                    spill past the fixed overlay itself, not just its own box. */}
                <p className="mt-8 text-2xl font-semibold text-white truncate max-w-xs mx-auto px-2" title={name}>{name}</p>
                <p className="mt-2 text-sm text-white/70 font-mono">{subtitle}</p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="pb-8 sm:pb-10">
            <div className="max-w-3xl mx-auto px-4">
              <div className="flex items-end justify-center gap-2 sm:gap-3 flex-wrap">
                <AudioControl
                  t={t}
                  muted={muted}
                  onToggleMute={() => setMuted(v => !v)}
                  mics={mics}
                  selectedMicId={selectedMicId}
                  setSelectedMicId={selectMic}
                  canPickMic={canPickMic}
                  onPickerOpen={ensureDeviceLabels}
                />
                <SpeakerControl
                  t={t}
                  speakerOn={speakerOn}
                  onToggleSpeaker={() => setSpeakerOn(v => !v)}
                  speakers={speakers}
                  selectedSpeakerId={selectedSpeakerId}
                  setSelectedSpeakerId={selectSpeaker}
                  canSwitchSpeaker={canSwitchSpeaker}
                  onPickerOpen={ensureDeviceLabels}
                />
                {mode === "video" && (
                  <ControlButton icon={cameraOn ? Video : VideoOff} label={cameraOn ? "Camera" : "Cam off"} active={!cameraOn} onClick={() => setCameraOn(v => !v)} />
                )}
                {isGroup && (
                  <ControlButton icon={UserPlus} label="Add" active={showAddPanel} onClick={() => setShowAddPanel(v => !v)} />
                )}
                <ControlButton icon={PhoneOff} label="End" danger onClick={onClose} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add participants side panel */}
      {isGroup && showAddPanel && (
        <div className="relative w-[300px] shrink-0 bg-zinc-900/95 backdrop-blur-md border-l border-white/10 flex flex-col">
          {/* Panel header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 shrink-0">
            <p className="flex-1 text-sm font-semibold text-white">Add to call</p>
            <button onClick={() => setShowAddPanel(false)} className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="px-3 py-2 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-1.5">
              <Search className="w-3.5 h-3.5 text-white/50 shrink-0" />
              <input
                autoFocus
                value={addSearch}
                onChange={e => setAddSearch(e.target.value)}
                placeholder="Search contacts…"
                className="flex-1 bg-transparent text-sm text-white placeholder:text-white/40 outline-none"
              />
              {addSearch && (
                <button onClick={() => setAddSearch("")}>
                  <X className="w-3 h-3 text-white/40" />
                </button>
              )}
            </div>
          </div>

          {/* Contact list */}
          <div className="flex-1 overflow-y-auto">
            {filteredContacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6 py-10">
                <p className="text-sm text-white/40">
                  {addSearch ? `No results for "${addSearch}"` : "No contacts available"}
                </p>
              </div>
            ) : (
              filteredContacts.map(c => {
                const isAdded = addedIds.has(c.id)
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      if (isAdded) return
                      setAddedIds(prev => new Set([...prev, c.id]))
                    }}
                    disabled={isAdded}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${isAdded ? "opacity-60 cursor-default" : "hover:bg-white/10"}`}
                  >
                    <div className="relative shrink-0">
                      <Avatar name={c.name} imageUrl={c.imageUrl} contactId={c.id} size={40} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-white truncate">{c.name}</p>
                      <p className="text-xs text-white/40">{isAdded ? "In call" : "Tap to add"}</p>
                    </div>
                    {isAdded ? (
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                        <UserPlus className="w-3 h-3 text-white/50" />
                      </div>
                    )}
                  </button>
                )
              })
            )}
          </div>

          {/* Added count footer */}
          {addedIds.size > 0 && (
            <div className="px-4 py-3 border-t border-white/10 shrink-0">
              <p className="text-xs text-white/50 text-center">{addedIds.size} participant{addedIds.size !== 1 ? "s" : ""} added</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
